import { getProgramWorkspaceScopeEntries, isProgramWorkspaceScopeDirty } from './programEditWorkspace';
import { saveProgramWorkspaceScopes } from './programUnifiedSave';

export type ProgramAttachmentCoSaveKind = 'slide' | 'worksheet';

export class ProgramTableCoSaveCancelledError extends Error {
  override name = 'ProgramTableCoSaveCancelledError';
  constructor() {
    super('프로그램 표 함께 저장을 취소했습니다.');
  }
}

export function isProgramTableCoSaveCancelled(error: unknown): boolean {
  return error instanceof ProgramTableCoSaveCancelledError ||
    (error instanceof Error && error.name === 'ProgramTableCoSaveCancelledError');
}

export function programTableDirtyWithAttachmentSaveConfirmMessage(
  kind: ProgramAttachmentCoSaveKind
): string {
  const label = kind === 'slide' ? '슬라이드' : '활동지';
  return `프로그램 표에 저장하지 않은 변경(순서·학습내용)이 있습니다. ${label}와 함께 저장할까요?`;
}

/** `track|ws|grade|year|term|scopeKey` — scopeKey는 `|`를 포함할 수 있음 */
export function scopeKeyFromProgramWorkspaceScopeId(scopeId: string): string {
  const parts = scopeId.trim().split('|');
  if (parts.length < 6) return '';
  if (parts[0] !== 'track' && parts[0] !== 'project') return '';
  return parts.slice(5).join('|');
}

/**
 * 슬라이드·활동지 저장 직전 LS를 다시 읽는다. 같은 표 스코프(+편제 동기화 fan-out)의 dirty만.
 * 네트워크 없음.
 */
export function collectDirtyWorkspaceScopeIdsForAttachmentSave(params: {
  scopeId?: string | null;
  workspaceId?: string | null;
  gradeId?: string | null;
  academicYear?: number;
  termNumber?: 1 | 2;
}): string[] {
  const scopeId = (params.scopeId ?? '').trim();
  const ids = new Set<string>();
  if (scopeId && isProgramWorkspaceScopeDirty(scopeId)) ids.add(scopeId);

  const ws = (params.workspaceId ?? '').trim();
  const gid = (params.gradeId ?? '').trim();
  const year = params.academicYear;
  const term = params.termNumber;
  if (!ws || !gid || year == null || (term !== 1 && term !== 2)) {
    return Array.from(ids);
  }

  const payloadScopeKey = scopeKeyFromProgramWorkspaceScopeId(scopeId);
  for (const entry of getProgramWorkspaceScopeEntries()) {
    if (!entry.isDirty) continue;
    if (entry.workspaceId.trim() !== ws || entry.gradeId.trim() !== gid) continue;
    if (entry.academicYear !== year || entry.termNumber !== term) continue;
    if (scopeId && entry.scopeId === scopeId) {
      ids.add(entry.scopeId);
      continue;
    }
    if (payloadScopeKey && entry.trackSyncFanoutScopeKeys?.includes(payloadScopeKey)) {
      ids.add(entry.scopeId);
    }
  }
  return Array.from(ids);
}

/**
 * 표 dirty면 확인 후 워크스페이스만 저장(첨부 flush 없음 — 슬라이드 연결은 표가 DB와 같아진 뒤).
 */
export async function confirmAndSaveDirtyProgramWorkspaceBeforeAttachment(params: {
  scopeId?: string | null;
  workspaceId?: string | null;
  gradeId?: string | null;
  academicYear?: number;
  termNumber?: 1 | 2;
  attachmentKind: ProgramAttachmentCoSaveKind;
  confirm?: (message: string) => boolean;
}): Promise<'proceed' | 'cancel'> {
  const ids = collectDirtyWorkspaceScopeIdsForAttachmentSave(params);
  if (!ids.length) return 'proceed';
  const message = programTableDirtyWithAttachmentSaveConfirmMessage(params.attachmentKind);
  const ok = (params.confirm ?? ((msg) => window.confirm(msg)))(message);
  if (!ok) return 'cancel';
  await saveProgramWorkspaceScopes(ids);
  return 'proceed';
}
